import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useConfirm } from './ConfirmDialog';
import { getDefaultDates } from '../utils/weekDates';
import { formatOrderDate, formatOrderTime } from '../utils/orderTime';
import 'bootstrap/dist/css/bootstrap.min.css';
import './WeeksOrders.css';

const EditOrder = () => {
  const defaults = getDefaultDates();
  const [pendingStart, setPendingStart] = useState(defaults.start);
  const [pendingEnd, setPendingEnd] = useState(defaults.end);
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [pendingItem, setPendingItem] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { token } = useAuth();
  const [confirm, confirmDialog] = useConfirm();

  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchOrders = async (start, end) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ startDate: start, endDate: end });
      const res = await fetch(
        `https://api.reshipesbakery.com/reshipes/getOrdersByDateRange?${params}`,
        { headers: authHeaders }
      );
      if (!res.ok) throw new Error('Failed to load orders');
      const data = await res.json();
      setOrders(data.orders);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Every item, not just the currently open menu: orders are usually edited
  // after the form has closed, when the open-menu list is empty.
  const fetchItems = async () => {
    try {
      const res = await fetch('https://api.reshipesbakery.com/item/getAllItems', {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Failed to load items');
      setItems(await res.json());
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    fetchOrders(defaults.start, defaults.end);
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // The same food name exists on several menus, so group the picker by menu.
  const itemsByMenu = items.reduce((groups, item) => {
    const menu = item.menuType || 'Other';
    (groups[menu] = groups[menu] || []).push(item);
    return groups;
  }, {});
  const menuNames = Object.keys(itemsByMenu).sort();

  const handleApply = () => fetchOrders(pendingStart, pendingEnd);
  const refresh = () => fetchOrders(pendingStart, pendingEnd);

  const handleUpdateQuantity = async (orderId, itemId, quantity) => {
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch('https://api.reshipesbakery.com/reshipes/updateUserOrder', {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ orderItem: { itemID: itemId, quantity }, orderId }),
      });
      if (!res.ok) throw new Error('Failed to update item');
      setSuccess(`Updated order #${orderId}`);
      refresh();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDeleteItem = async (orderId, itemId) => {
    if (!(await confirm({ message: 'Remove this item from the order?', confirmLabel: 'Remove' }))) return;
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch('https://api.reshipesbakery.com/reshipes/deleteFromUserOrder', {
        method: 'DELETE',
        headers: authHeaders,
        body: JSON.stringify({ orderItem: { itemID: itemId }, orderId }),
      });
      if (!res.ok) {
        throw new Error(res.status === 404
          ? 'That item is no longer on the order. Refresh the page to see the current orders.'
          : 'Failed to remove item');
      }
      setSuccess(`Removed item from order #${orderId}`);
      refresh();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleAddItem = async (orderId) => {
    const pending = pendingItem[orderId];
    if (!pending?.itemId || !pending?.quantity) return;
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch('https://api.reshipesbakery.com/reshipes/addToUserOrder', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          orderItem: { itemID: parseInt(pending.itemId), quantity: parseInt(pending.quantity) },
          orderId,
        }),
      });
      if (!res.ok) throw new Error('Failed to add item');
      setSuccess(`Added item to order #${orderId}`);
      setPendingItem((prev) => ({ ...prev, [orderId]: { itemId: '', quantity: '' } }));
      refresh();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDeleteOrder = async (orderId, customerName) => {
    const ok = await confirm({
      message: `Delete the entire order #${orderId} for "${customerName}"? This cannot be undone.`,
      confirmLabel: 'Delete Order',
    });
    if (!ok) return;
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`https://api.reshipesbakery.com/reshipes/deleteOrder?orderId=${orderId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(res.status === 404
          ? `Order #${orderId} no longer exists. Refresh the page to see the current orders.`
          : 'Failed to delete order');
      }
      setSuccess(`Deleted order #${orderId}`);
      refresh();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="container mt-5">
      {confirmDialog}
      <h1>Edit Orders</h1>
      {(error || success) && (
        <div className="order-feedback">
          {error && <div className="alert alert-danger mb-2">{error}</div>}
          {success && <div className="alert alert-success mb-2">{success}</div>}
        </div>
      )}

      <div className="mt-3 d-flex flex-wrap gap-3 align-items-end">
        <div>
          <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>From</label>
          <input type="date" className="form-control form-control-sm"
            value={pendingStart} onChange={(e) => setPendingStart(e.target.value)} />
        </div>
        <div>
          <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>To</label>
          <input type="date" className="form-control form-control-sm"
            value={pendingEnd} onChange={(e) => setPendingEnd(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={handleApply}>Apply</button>
      </div>

      {loading && <div className="mt-4">Loading…</div>}
      {!loading && orders.length === 0 && <p className="mt-4">No orders found for this period.</p>}

      {!loading && orders.map((order) => (
        <div key={order.orderId} className="mt-4 p-3 border rounded">
          <h3>
            {order.customerName} <small className="text-muted">(Order #{order.orderId})</small>
          </h3>
          <p className="mb-1">Order Date: {formatOrderDate(order.orderDate, order.orderTime)}</p>
          <p className="mb-1">Order Time: {formatOrderTime(order.orderDate, order.orderTime)}</p>
          {order.comments && <p className="mb-1">Comments: {order.comments}</p>}

          <table className="table table-striped order-items-table mt-3">
            <thead>
              <tr>
                <th>Food Name</th>
                <th>Quantity</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, index) => (
                <tr key={item.itemId ?? `${order.orderId}-${index}`}>
                  <td>{item.size} {item.foodName}</td>
                  <td style={{ maxWidth: 100 }}>
                    <input
                      type="number"
                      min="1"
                      className="form-control form-control-sm"
                      defaultValue={item.quantity}
                      disabled={!item.itemId}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value);
                        if (item.itemId && value > 0 && value !== item.quantity) {
                          handleUpdateQuantity(order.orderId, item.itemId, value);
                        }
                      }}
                    />
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      disabled={!item.itemId}
                      onClick={() => handleDeleteItem(order.orderId, item.itemId)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="d-flex gap-2 align-items-end flex-wrap mb-3">
            <div>
              <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Add Item</label>
              <select
                className="form-control form-control-sm"
                value={pendingItem[order.orderId]?.itemId || ''}
                onChange={(e) =>
                  setPendingItem((prev) => ({
                    ...prev,
                    [order.orderId]: { ...prev[order.orderId], itemId: e.target.value },
                  }))
                }
              >
                <option value="">Select an item</option>
                {menuNames.map((menu) => (
                  <optgroup key={menu} label={menu}>
                    {itemsByMenu[menu].map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.size} {item.foodName} (${item.price.toFixed(2)})
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Quantity</label>
              <input
                type="number"
                min="1"
                className="form-control form-control-sm"
                style={{ width: 90 }}
                value={pendingItem[order.orderId]?.quantity || ''}
                onChange={(e) =>
                  setPendingItem((prev) => ({
                    ...prev,
                    [order.orderId]: { ...prev[order.orderId], quantity: e.target.value },
                  }))
                }
              />
            </div>
            <button className="btn btn-sm btn-primary" onClick={() => handleAddItem(order.orderId)}>
              Add
            </button>
          </div>

          <h5>Total: ${order.orderTotal.toFixed(2)}</h5>
          <button
            className="btn btn-danger btn-sm mt-2"
            onClick={() => handleDeleteOrder(order.orderId, order.customerName)}
          >
            Delete Entire Order
          </button>
        </div>
      ))}
    </div>
  );
};

export default EditOrder;
